import {
  adminFoodDictionaryControllerSearchFoods,
  adminFoodDictionaryControllerGetFood,
  adminFoodDictionaryControllerCreateFood,
  adminFoodDictionaryControllerUpdateFood,
  adminFoodDictionaryControllerGetUsage,
  adminFoodDictionaryControllerDeleteFood,
  adminFoodDictionaryControllerAddServing,
  adminFoodDictionaryControllerUpdateServing,
  adminFoodDictionaryControllerRemoveServing,
  adminFoodDictionaryControllerGetNutrientDefinitions,
} from "../api/generated";

/**
 * The food catalog as a company admin, manager or trainer sees it.
 *
 * <p>Reads are wider than writes, deliberately. The search returns the shared system catalog
 * <em>plus</em> this company's own foods, because a coach building a menu needs both; writing only
 * ever touches the company's own, and the server refuses anything else. The company is never sent —
 * it comes from the logged-in user server-side, so there is no company id to get wrong here.
 *
 * <p>Generated SDK ops throughout. Each returns {@code {data}}, matching the envelope the rest of the
 * admin services expose, so callers read {@code res.data} as they always have.
 *
 * <p>The system-admin equivalents live in {@code services/system/FoodDictionaryService.js} and hit
 * {@code /system/food-dictionary}, which is unscoped and can also change a food's scope.
 */

/**
 * Present a catalog food in the shape the menu builder already speaks.
 *
 * <p>The builder was written against the legacy `FullAdminFood`, whose serving list is
 * `availableServingSizes` and whose default macros sit on `nutritionValues`. The catalog DTO names
 * those `servings` and `defaultServingNutrition`. Translating here rather than in the component keeps
 * the rename in one place, so the builder doesn't have to know the catalog moved.
 */
const toBuilderShape = (dto) => ({
  id: dto.id,
  name: dto.name,
  brand: dto.brand,
  barcode: dto.barcode,
  certified: dto.certified,
  scope: dto.scope,
  imageUrl: dto.imageUrl,
  nutritionValues: dto.defaultServingNutrition,
  availableServingSizes: (dto.servings || []).map((s) => ({
    id: s.id,
    servingName: s.name,
    gramsPerServing: s.gramsPerServing,
    isDefault: s.isDefault,
    nutritionValues: s.nutritionValues,
    micronutrients: s.micronutrients,
  })),
});

/**
 * System foods + this company's own. Never another company's, never a trainee's private food.
 *
 * <p>The parameter shape is spelled out because TypeScript infers a destructured signature from the
 * properties that carry defaults — `keyword` and `barcode` would otherwise vanish from the type, and
 * every `.tsx` caller passing one fails to compile.
 *
 * @param {{ keyword?: string, barcode?: string, ownOnly?: boolean, page?: number, size?: number }} [params]
 */
export const searchFoods = async ({ keyword, barcode, ownOnly = false, page = 0, size = 20 } = {}) => {
  const { data, error } = await adminFoodDictionaryControllerSearchFoods({
    query: {
      page,
      size,
      ...(ownOnly ? { ownOnly: true } : {}),
      ...(keyword && keyword.trim() !== "" ? { keyword: keyword.trim() } : {}),
      ...(barcode && barcode.trim() !== "" ? { barcode: barcode.trim() } : {}),
    },
  });
  if (error) throw error;
  return { data: { ...data, content: (data?.content || []).map(toBuilderShape) } };
};

/**
 * The same search, in the catalog's own shape rather than the menu builder's.
 *
 * <p>{@link searchFoods} maps every row through `toBuilderShape`, which renames `servings` to
 * `availableServingSizes` and `defaultServingNutrition` to `nutritionValues` for the menu builder.
 * The catalog screen reads the original names, so going through that adapter left its portions,
 * calories and protein columns showing "—" for every row. This returns the DTO as the server sends
 * it.
 *
 * @param {{ keyword?: string, barcode?: string, ownOnly?: boolean, certified?: boolean|null,
 *           page?: number, size?: number }} [params]
 */
export const searchCatalog = async (
  { keyword, barcode, ownOnly = false, certified = null, page = 0, size = 20 } = {}
) => {
  const { data, error } = await adminFoodDictionaryControllerSearchFoods({
    query: {
      page,
      size,
      ...(ownOnly ? { ownOnly: true } : {}),
      ...(certified === null || certified === undefined ? {} : { certified }),
      ...(keyword && keyword.trim() !== "" ? { keyword: keyword.trim() } : {}),
      ...(barcode && barcode.trim() !== "" ? { barcode: barcode.trim() } : {}),
    },
  });
  if (error) throw error;
  return { data };
};

/** One food — this company's own, or a system food. 404 on anything else. */
export const getFoodById = async (id) => {
  const { data, error } = await adminFoodDictionaryControllerGetFood({ path: { id } });
  if (error) throw error;
  return { data };
};

/**
 * Create a food owned by the caller's company.
 *
 * <p>Returns the `{success, message}` envelope the legacy admin service used, because the meal-plan
 * dialogs already branch on it. The payload is translated from the legacy `AdminFood` shape — which
 * carried its first portion in a `servingSizes` array — onto the catalog's flat create request.
 */
export const createFood = async (foodData) => {
  const first = (foodData.servingSizes && foodData.servingSizes[0]) || {};
  const payload = {
    name: foodData.name,
    brand: foodData.brand || null,
    barcode: foodData.barcode || null,
    servingName: foodData.servingName || first.name || first.servingName || "מנה",
    gramsPerServing: foodData.gramsPerServing ?? first.gramsPerServing ?? null,
    calories: foodData.calories ?? first.calories ?? null,
    protein: foodData.protein ?? first.protein ?? null,
    carbs: foodData.carbs ?? first.carbs ?? null,
    fats: foodData.fats ?? first.fats ?? null,
  };
  const { data, error } = await adminFoodDictionaryControllerCreateFood({ body: payload });
  if (error) {
    return { success: false, message: error?.errorMessage || "שגיאה ביצירת המזון" };
  }
  return { success: true, data, message: "המזון נוצר בהצלחה" };
};

/** Edit a food this company owns. 403 on a system food or another company's. */
export const updateFood = async (id, payload) => {
  const { data, error } = await adminFoodDictionaryControllerUpdateFood({ path: { id }, body: payload });
  if (error) throw error;
  return { data };
};

/**
 * How widely a food is used, so a destructive edit can say what it will affect first.
 *
 * <p>Counts are scoped to the caller's company — the blast radius they actually control. Logged meals
 * are reported but never changed by anything here.
 */
export const getFoodUsage = async (id) => {
  const { data, error } = await adminFoodDictionaryControllerGetUsage({ path: { id } });
  if (error) throw error;
  return { data };
};

/**
 * Delete a food and remove it from this company's menus.
 *
 * <p>Without `confirm` the server answers 409 and returns the usage counts instead of deleting, so the
 * UI can show what is about to happen. Call again with `confirm: true` once the person agrees.
 */
export const deleteFood = async (id, { confirm = false } = {}) => {
  const { data, error } = await adminFoodDictionaryControllerDeleteFood({
    path: { id },
    query: { confirm },
  });
  if (error) throw error;
  return { data };
};

/**
 * Portion editing for this company's own foods.
 *
 * <p>The `/system` twins of these three can reach any food in the catalog and are refused outright
 * for a non-system-admin. These are the company-scoped door: the server checks ownership on every
 * one, so a system food — which no company owns — comes back 403 rather than being edited. The UI
 * hides the controls as well, but that is a courtesy; this is where it is actually decided.
 */
export const addServing = async (foodId, payload) => {
  const { data, error } = await adminFoodDictionaryControllerAddServing({
    path: { id: foodId },
    body: payload,
  });
  if (error) throw error;
  return { data };
};

export const updateServing = async (foodId, servingId, payload) => {
  const { data, error } = await adminFoodDictionaryControllerUpdateServing({
    path: { id: foodId, servingId },
    body: payload,
  });
  if (error) throw error;
  return { data };
};

/** Soft-deletes the portion: menus already built on it keep resolving, it stops being offered. */
export const removeServing = async (foodId, servingId) => {
  const { data, error } = await adminFoodDictionaryControllerRemoveServing({
    path: { id: foodId, servingId },
  });
  if (error) throw error;
  return { data };
};

/** The micronutrient catalogue the serving editor keys its values by. Same list everywhere. */
export const getNutrientDefinitions = async () => {
  const { data, error } = await adminFoodDictionaryControllerGetNutrientDefinitions();
  if (error) throw error;
  return { data };
};
